import {type FunctionMetadata, metadataFromFunctionCall} from '@github-ui/copilot-chat/components/FunctionLoadingUtils'
import type {SkillExecution} from '@github-ui/copilot-chat/utils/copilot-chat-types'

// Each inline tool call has at most two semantic labels:
//
// - `definitionText` describes the tool call itself ("Get file X"). Used
//   whenever the expanded body can only show the call arguments — i.e. while
//   pending, after an empty completion, or after a failure.
// - `referenceText` describes the result the user can actually view
//   ("View file X"). Used only once the call has produced renderable
//   references.
//
// Anything else (shimmer wording, failed prefix) is derived from these two
// in InlineToolCall so we don't repeat the same phrase five times per skill.
export interface InlineFunctionMetadata extends FunctionMetadata {
  definitionText?: string
  referenceText?: string
}

export function inlineToolCallMetadata(skillExecution: SkillExecution | undefined): InlineFunctionMetadata | undefined {
  const base = metadataFromFunctionCall(skillExecution)
  if (!base || !skillExecution) return base

  const args = parseArguments(skillExecution.arguments)

  switch (skillExecution.slug) {
    case 'getfile': {
      const target = args.path || args.repo || 'file'
      return {...base, definitionText: `Get file ${target}`, referenceText: `View file ${target}`}
    }

    case 'getfilechanges': {
      const target = args.path || args.repo || 'file'
      return {
        ...base,
        definitionText: `Get file changes for ${target}`,
        referenceText: `View file changes for ${target}`,
      }
    }

    case 'get-github-data': {
      const contentsPath = extractContentsPath(args.endpoint)
      if (contentsPath) {
        return {
          ...base,
          definitionText: `View directory ${contentsPath}`,
          referenceText: `View directory ${contentsPath}`,
        }
      }
      // The expanded panel shows the call rather than its results, so describe
      // the call ("Get data from …") instead of base's "Results of …" wording.
      if (!args.endpoint) return base
      return {...base, definitionText: `Get data from ${args.endpoint}`}
    }

    case 'lexical-code-search':
    case 'lexical-code-search-githubread': {
      const scope = formatScope(args.scopingQuery)
      const target = scope ? `${args.query ?? ''} in ${scope}` : (args.query ?? '')
      return {
        ...base,
        definitionText: `Search ${target}`.trim(),
        referenceText: `View search results for ${target}`.trim(),
      }
    }

    case 'semantic-code-search': {
      const scope = args.repoOwner && args.repoName ? `${args.repoOwner}/${args.repoName}` : undefined
      const target = scope ? `${args.query ?? ''} in ${scope}` : (args.query ?? '')
      return {
        ...base,
        definitionText: `Search ${target}`.trim(),
        referenceText: `View search results for ${target}`.trim(),
      }
    }

    default:
      return base
  }
}

function formatScope(scopingQuery: string | undefined): string | undefined {
  if (!scopingQuery) return undefined
  return scopingQuery.trim() || undefined
}

// Matches REST endpoints like `/repos/{owner}/{repo}/contents/{path}` and
// returns just `{path}` so the label can drop the `/repos/...` prefix.
function extractContentsPath(endpoint: string | undefined): string | undefined {
  if (!endpoint) return undefined
  const match = endpoint.match(/^\/?repos\/[^/]+\/[^/]+\/contents\/(.+?)\/?$/)
  return match?.[1]
}

interface KnownArguments {
  path?: string
  repo?: string
  endpoint?: string
  query?: string
  scopingQuery?: string
  repoOwner?: string
  repoName?: string
}

function parseArguments(raw: string | undefined): KnownArguments {
  if (!raw) return {}
  try {
    return JSON.parse(raw) as KnownArguments
  } catch {
    return {}
  }
}
